import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Search, Filter, Download, Upload, Package, DollarSign, 
  ChevronDown, ChevronUp, MoreHorizontal, X, ArrowUpDown,
  Layers, Building2, CheckCircle2, Clock, AlertCircle
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Skeleton } from '../components/ui/skeleton';
import apiClient from '../api/client';
import { importApi } from '../api/import';
import ImportOpportunitiesDialog from '../components/opportunities/ImportOpportunitiesDialog';

// Status badge configurations
const STATUS_CONFIG = {
  'Active': { color: 'bg-green-100 text-green-800 border-green-200', icon: CheckCircle2 },
  'Awarded': { color: 'bg-blue-100 text-blue-800 border-blue-200', icon: CheckCircle2 },
  'Quoted': { color: 'bg-amber-100 text-amber-800 border-amber-200', icon: Clock },
  'In Progress': { color: 'bg-purple-100 text-purple-800 border-purple-200', icon: Clock },
  'Lost': { color: 'bg-red-100 text-red-800 border-red-200', icon: AlertCircle },
  'default': { color: 'bg-gray-100 text-gray-800 border-gray-200', icon: null }
};

export default function PartsListPage() {
  const navigate = useNavigate();
  
  // Data state
  const [parts, setParts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [summary, setSummary] = useState({ total_parts: 0, total_annual_revenue_cents: 0 });
  const [filterOptions, setFilterOptions] = useState({ oems: [], segments: [], statuses: [] });
  
  // Filter state
  const [searchQuery, setSearchQuery] = useState('');
  const [oemFilter, setOemFilter] = useState('all');
  const [segmentFilter, setSegmentFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  
  // Sorting state
  const [sortBy, setSortBy] = useState('part_number');
  const [sortOrder, setSortOrder] = useState('asc');
  
  // UI state
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [searchTimeout, setSearchTimeout] = useState(null);

  // Fetch parts with filters
  const fetchParts = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const params = new URLSearchParams({
        limit: '100',
        offset: '0',
        sort_by: sortBy,
        sort_order: sortOrder
      });
      
      if (searchQuery && searchQuery.length >= 2) {
        params.append('search', searchQuery);
      }
      if (oemFilter && oemFilter !== 'all') {
        params.append('oem_filter', oemFilter);
      }
      if (segmentFilter && segmentFilter !== 'all') {
        params.append('segment_filter', segmentFilter);
      }
      if (statusFilter && statusFilter !== 'all') {
        params.append('status_filter', statusFilter);
      }
      
      const response = await apiClient.get(`/api/v1/opportunities?${params}`);
      const data = response.data;
      
      if (data.success) {
        setParts(data.data || []);
        setSummary(data.summary || { total_parts: 0, total_annual_revenue_cents: 0 });
        setFilterOptions(data.filter_options || { oems: [], segments: [], statuses: [] });
      } else {
        setError(data.error || 'Failed to fetch parts');
      }
    } catch (err) {
      setError(err.message || 'Failed to fetch parts');
    } finally {
      setLoading(false);
    }
  };

  // Debounced search
  useEffect(() => {
    if (searchTimeout) clearTimeout(searchTimeout);
    
    if (searchQuery.length === 0 || searchQuery.length >= 2) {
      const timeout = setTimeout(() => {
        fetchParts();
      }, searchQuery.length === 0 ? 0 : 400);
      setSearchTimeout(timeout);
      return () => clearTimeout(timeout);
    }
  }, [searchQuery, oemFilter, segmentFilter, statusFilter, sortBy, sortOrder]);

  // Initial load
  useEffect(() => {
    fetchParts();
  }, []);

  // Format currency
  const formatCurrency = (cents) => {
    if (!cents) return '$0';
    const amount = cents / 100;
    if (amount >= 1000000) {
      return `$${(amount / 1000000).toFixed(1)}M`;
    } else if (amount >= 1000) {
      return `$${(amount / 1000).toFixed(0)}K`;
    }
    return `$${amount.toFixed(0)}`;
  };

  // Format volume
  const formatVolume = (volume) => {
    if (!volume) return '0';
    return volume.toLocaleString('en-US');
  };

  // Handle sort
  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('asc');
    }
  };

  // Get sort icon
  const getSortIcon = (column) => {
    if (sortBy !== column) return <ArrowUpDown className="h-4 w-4 ml-1 text-gray-400" />;
    return sortOrder === 'asc' 
      ? <ChevronUp className="h-4 w-4 ml-1 text-blue-600" />
      : <ChevronDown className="h-4 w-4 ml-1 text-blue-600" />;
  };

  // Get status badge
  const getStatusBadge = (status) => {
    const config = STATUS_CONFIG[status] || STATUS_CONFIG['default'];
    const Icon = config.icon;
    return (
      <Badge className={`${config.color} border font-medium flex items-center gap-1`}>
        {Icon && <Icon className="h-3 w-3" />}
        {status || 'Not Set'}
      </Badge>
    );
  };

  // Clear all filters
  const clearFilters = () => {
    setSearchQuery('');
    setOemFilter('all');
    setSegmentFilter('all');
    setStatusFilter('all');
  };

  // Check if any filters are active
  const hasActiveFilters = searchQuery || oemFilter !== 'all' || segmentFilter !== 'all' || statusFilter !== 'all';

  // Handle export
  const handleExport = async () => {
    try {
      await importApi.exportOpportunities();
    } catch (error) {
      console.error('Error exporting:', error);
      alert('Failed to export data');
    }
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Parts</h1>
          <p className="mt-1 text-gray-600">
            Manage your parts catalog with volume and pricing data
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleExport}>
            <Download className="w-4 h-4 mr-2" />
            Export
          </Button>
          <Button onClick={() => setShowImportDialog(true)}>
            <Upload className="w-4 h-4 mr-2" />
            Import
          </Button>
        </div>
      </div>

      {/* Summary Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card className="bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-blue-600">Total Parts</p>
                <p className="text-3xl font-bold text-blue-900">
                  {loading ? <Skeleton className="h-9 w-20" /> : summary.total_parts.toLocaleString()}
                </p>
              </div>
              <div className="h-12 w-12 rounded-full bg-blue-100 flex items-center justify-center">
                <Package className="h-6 w-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-green-50 to-emerald-50 border-green-200">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-green-600">Annual Revenue</p>
                <p className="text-3xl font-bold text-green-900">
                  {loading ? <Skeleton className="h-9 w-28" /> : formatCurrency(summary.total_annual_revenue_cents)}
                </p>
              </div>
              <div className="h-12 w-12 rounded-full bg-green-100 flex items-center justify-center">
                <DollarSign className="h-6 w-6 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Table Card */}
      <Card>
        <CardHeader className="pb-4">
          <div className="flex flex-col gap-4">
            {/* Search and Filters Row */}
            <div className="flex flex-col sm:flex-row gap-3">
              {/* Search */}
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                <Input
                  placeholder="Search parts by number, description, OEM..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
              
              {/* Filter Dropdowns */}
              <div className="flex gap-2 flex-wrap">
                {/* OEM Filter */}
                <Select value={oemFilter} onValueChange={setOemFilter}>
                  <SelectTrigger className="w-[140px]">
                    <Building2 className="h-4 w-4 mr-2 text-gray-500" />
                    <SelectValue placeholder="OEM" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All OEMs</SelectItem>
                    {filterOptions.oems.map(oem => (
                      <SelectItem key={oem} value={oem}>{oem}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                
                {/* Segment Filter */}
                <Select value={segmentFilter} onValueChange={setSegmentFilter}>
                  <SelectTrigger className="w-[160px]">
                    <Layers className="h-4 w-4 mr-2 text-gray-500" />
                    <SelectValue placeholder="Segment" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Segments</SelectItem>
                    {filterOptions.segments.map(seg => (
                      <SelectItem key={seg.id || seg} value={seg.id || seg}>
                        {seg.name || seg}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                
                {/* Status Filter */}
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-[140px]">
                    <Filter className="h-4 w-4 mr-2 text-gray-500" />
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    {filterOptions.statuses.map(status => (
                      <SelectItem key={status} value={status}>{status}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                
                {/* Clear Filters */}
                {hasActiveFilters && (
                  <Button variant="ghost" size="icon" onClick={clearFilters} className="text-gray-500">
                    <X className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </div>
            
            {/* Results Count */}
            <div className="flex items-center justify-between text-sm text-gray-600">
              <span>
                {loading ? 'Loading...' : `Showing ${parts.length} of ${summary.total_parts} parts`}
              </span>
              {hasActiveFilters && (
                <Badge variant="outline" className="text-blue-600 border-blue-200 bg-blue-50">
                  Filters active
                </Badge>
              )}
            </div>
          </div>
        </CardHeader>
        
        <CardContent className="p-0">
          {/* Table */}
          <div className="rounded-lg border-t">
            <Table>
              <TableHeader>
                <TableRow className="bg-gray-50/80">
                  <TableHead 
                    className="cursor-pointer hover:bg-gray-100 transition-colors"
                    onClick={() => handleSort('part_number')}
                  >
                    <div className="flex items-center font-semibold">
                      Part Number
                      {getSortIcon('part_number')}
                    </div>
                  </TableHead>
                  <TableHead 
                    className="cursor-pointer hover:bg-gray-100 transition-colors"
                    onClick={() => handleSort('description')}
                  >
                    <div className="flex items-center font-semibold">
                      Description
                      {getSortIcon('description')}
                    </div>
                  </TableHead>
                  <TableHead 
                    className="cursor-pointer hover:bg-gray-100 transition-colors"
                    onClick={() => handleSort('oem')}
                  >
                    <div className="flex items-center font-semibold">
                      OEM
                      {getSortIcon('oem')}
                    </div>
                  </TableHead>
                  <TableHead 
                    className="cursor-pointer hover:bg-gray-100 transition-colors"
                    onClick={() => handleSort('segment')}
                  >
                    <div className="flex items-center font-semibold">
                      Segment
                      {getSortIcon('segment')}
                    </div>
                  </TableHead>
                  <TableHead className="text-right font-semibold">Volume</TableHead>
                  <TableHead className="text-right font-semibold">Avg Annual Revenue</TableHead>
                  <TableHead 
                    className="cursor-pointer hover:bg-gray-100 transition-colors"
                    onClick={() => handleSort('status')}
                  >
                    <div className="flex items-center font-semibold">
                      Status
                      {getSortIcon('status')}
                    </div>
                  </TableHead>
                  <TableHead className="w-10"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  // Loading skeletons
                  Array.from({ length: 5 }).map((_, i) => (
                    <TableRow key={i}>
                      <TableCell><Skeleton className="h-5 w-24" /></TableCell>
                      <TableCell><Skeleton className="h-5 w-40" /></TableCell>
                      <TableCell><Skeleton className="h-5 w-16" /></TableCell>
                      <TableCell><Skeleton className="h-5 w-20" /></TableCell>
                      <TableCell><Skeleton className="h-5 w-16" /></TableCell>
                      <TableCell><Skeleton className="h-5 w-20" /></TableCell>
                      <TableCell><Skeleton className="h-5 w-16" /></TableCell>
                      <TableCell><Skeleton className="h-5 w-8" /></TableCell>
                    </TableRow>
                  ))
                ) : error ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-8 text-red-500">
                      Error: {error}
                    </TableCell>
                  </TableRow>
                ) : parts.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-12 text-gray-500">
                      <Package className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                      <p className="font-medium">No parts found</p>
                      <p className="text-sm mt-1">Try adjusting your search or filters</p>
                    </TableCell>
                  </TableRow>
                ) : (
                  parts.map((part) => (
                    <TableRow 
                      key={part.id} 
                      className="hover:bg-blue-50/50 cursor-pointer transition-colors"
                      onClick={() => navigate(`/parts/${part.part_number}`)}
                    >
                      <TableCell className="font-medium text-blue-600">
                        {part.part_number}
                      </TableCell>
                      <TableCell className="text-gray-700 max-w-[250px] truncate">
                        {part.description}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="bg-gray-50">
                          {part.oem}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge className="bg-purple-100 text-purple-800 border-purple-200">
                          {part.segment || 'Unassigned'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right font-medium">
                        {formatVolume(part.volume)}
                      </TableCell>
                      <TableCell className="text-right font-semibold text-green-700">
                        {formatCurrency(part.avg_annual_revenue_cents)}
                      </TableCell>
                      <TableCell>
                        {getStatusBadge(part.business_status)}
                      </TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                            <Button variant="ghost" size="icon" className="h-8 w-8">
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/parts/${part.part_number}`);
                            }}>
                              View Details
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/parts/${part.part_number}?edit=true`);
                            }}>
                              Edit Part
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Import Dialog */}
      <ImportOpportunitiesDialog
        open={showImportDialog}
        onClose={() => setShowImportDialog(false)}
        onSuccess={() => {
          setShowImportDialog(false);
          fetchParts();
        }}
      />
    </div>
  );
}
